import { MapPin, Users } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { Button } from '@/components/ui/button';

interface SearchEngineFacadeProps {
  onInteraction: () => void;
  id?: string;
}

export const SearchEngineFacade = ({ onInteraction, id }: SearchEngineFacadeProps) => {
  const { t } = useLanguage();

  return (
    <div 
      className="w-full max-w-7xl mx-auto bg-white rounded-2xl shadow-lg p-4 md:p-6 relative z-10 font-jakarta"
      onClick={onInteraction}
      onFocus={onInteraction}
      onTouchStart={onInteraction}
      onMouseEnter={onInteraction}
      id={id}
    >
      {/* Mobile Layout */}
      <div className="lg:hidden space-y-3">
        {/* City Selector */}
        <div className="space-y-1.5">
          <label className="text-xs font-medium flex items-center gap-1.5 text-muted-foreground">
            <MapPin className="w-3.5 h-3.5" />
            {t('Location')}
          </label>
          <div 
            className="h-11 w-full border border-input rounded-md px-3 py-2 text-sm flex items-center justify-between text-muted-foreground cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary"
            aria-label={t('Select city')}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && onInteraction()}
          >
            {t('Select city')}
            <svg width="15" height="15" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 opacity-50"><path d="M4.93179 5.43179C4.75605 5.60753 4.75605 5.89245 4.93179 6.06819C5.10753 6.24392 5.39245 6.24392 5.56819 6.06819L7.49999 4.13638L9.43179 6.06819C9.60753 6.24392 9.89245 6.24392 10.0682 6.06819C10.2439 5.89245 10.2439 5.60753 10.0682 5.43179L7.81819 3.18179C7.73379 3.0974 7.61933 3.04999 7.49999 3.04999C7.38064 3.04999 7.26618 3.0974 7.18179 3.18179L4.93179 5.43179ZM10.0682 9.56819C10.2439 9.39245 10.2439 9.10753 10.0682 8.93179C9.89245 8.75606 9.60753 8.75606 9.43179 8.93179L7.49999 10.8636L5.56819 8.93179C5.39245 8.75606 5.10753 8.75606 4.93179 8.93179C4.75605 9.10753 4.75605 9.39245 4.93179 9.56819L7.18179 11.8182C7.26618 11.9026 7.38064 11.95 7.49999 11.95C7.61933 11.95 7.73379 11.9026 7.81819 11.8182L10.0682 9.56819Z" fill="currentColor" fillRule="evenodd" clipRule="evenodd"></path></svg>
          </div>
        </div>

        {/* Dates - Side by Side */}
        <div className="w-full h-auto py-3 px-3 border border-input rounded-md flex items-center justify-between gap-3 bg-white">
          <div className="flex-1 text-left min-w-0">
            <div className="text-[10px] font-medium text-muted-foreground mb-0.5">
              {t('Start Date')}
            </div>
            <div className="text-xs font-normal text-muted-foreground truncate">
              {t('Pick date')}
            </div>
          </div>
          
          <div className="h-10 w-px bg-border flex-shrink-0"></div>
          
          <div className="flex-1 text-left min-w-0">
            <div className="text-[10px] font-medium text-muted-foreground mb-0.5">
              {t('End Date')}
            </div>
            <div className="text-xs font-normal text-muted-foreground truncate">
              {t('Pick date')}
            </div>
          </div>
        </div>

        {/* Passengers */}
        <div className="space-y-1.5">
          <label className="text-xs font-medium flex items-center gap-1.5 text-muted-foreground">
            <Users className="w-3.5 h-3.5" />
            {t('Passengers')}
          </label>
          <div 
            className="h-11 w-full border border-input rounded-md px-3 py-2 text-sm flex items-center justify-between text-muted-foreground cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary"
            aria-label={t('Select passengers')}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && onInteraction()}
          >
            {t('Select passengers')}
            <svg width="15" height="15" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 opacity-50"><path d="M4.93179 5.43179C4.75605 5.60753 4.75605 5.89245 4.93179 6.06819C5.10753 6.24392 5.39245 6.24392 5.56819 6.06819L7.49999 4.13638L9.43179 6.06819C9.60753 6.24392 9.89245 6.24392 10.0682 6.06819C10.2439 5.89245 10.2439 5.60753 10.0682 5.43179L7.81819 3.18179C7.73379 3.0974 7.61933 3.04999 7.49999 3.04999C7.38064 3.04999 7.26618 3.0974 7.18179 3.18179L4.93179 5.43179ZM10.0682 9.56819C10.2439 9.39245 10.2439 9.10753 10.0682 8.93179C9.89245 8.75606 9.60753 8.75606 9.43179 8.93179L7.49999 10.8636L5.56819 8.93179C5.39245 8.75606 5.10753 8.75606 4.93179 8.93179C4.75605 9.10753 4.75605 9.39245 4.93179 9.56819L7.18179 11.8182C7.26618 11.9026 7.38064 11.95 7.49999 11.95C7.61933 11.95 7.73379 11.9026 7.81819 11.8182L10.0682 9.56819Z" fill="currentColor" fillRule="evenodd" clipRule="evenodd"></path></svg>
          </div>
        </div>

        {/* Pet Allowed Checkbox */}
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 border border-gray-300 rounded" />
          <label className="text-xs font-medium text-foreground">
            {t('Pet Allowed')}
          </label>
        </div>

        {/* Search Button */}
        <Button className="w-full bg-gradient-to-r from-primary to-accent hover:opacity-90 transition-opacity text-sm h-11 font-medium rounded-lg">
          {t('Search Caravans')}
        </Button>
      </div>

      {/* Desktop Layout - Horizontal */}
      <div className="hidden lg:flex items-stretch gap-0">
        {/* City Select */}
        <div className="flex-1 px-5 py-4 flex flex-col justify-between">
          <label className="text-[13px] font-semibold text-black block mb-3">
            {t('Locations')}
          </label>
          <div 
            className="h-9 text-[13px] flex items-center justify-between text-gray-500 cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary rounded-sm"
            aria-label={t('Select your city')}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && onInteraction()}
          >
            {t('Select your city')}
            <svg width="15" height="15" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 opacity-50"><path d="M4.93179 5.43179C4.75605 5.60753 4.75605 5.89245 4.93179 6.06819C5.10753 6.24392 5.39245 6.24392 5.56819 6.06819L7.49999 4.13638L9.43179 6.06819C9.60753 6.24392 9.89245 6.24392 10.0682 6.06819C10.2439 5.89245 10.2439 5.60753 10.0682 5.43179L7.81819 3.18179C7.73379 3.0974 7.61933 3.04999 7.49999 3.04999C7.38064 3.04999 7.26618 3.0974 7.18179 3.18179L4.93179 5.43179ZM10.0682 9.56819C10.2439 9.39245 10.2439 9.10753 10.0682 8.93179C9.89245 8.75606 9.60753 8.75606 9.43179 8.93179L7.49999 10.8636L5.56819 8.93179C5.39245 8.75606 5.10753 8.75606 4.93179 8.93179C4.75605 9.10753 4.75605 9.39245 4.93179 9.56819L7.18179 11.8182C7.26618 11.9026 7.38064 11.95 7.49999 11.95C7.61933 11.95 7.73379 11.9026 7.81819 11.8182L10.0682 9.56819Z" fill="currentColor" fillRule="evenodd" clipRule="evenodd"></path></svg>
          </div>
        </div>

        {/* Vertical Divider */}
        <div className="self-center h-12 w-[1px] bg-gray-200"></div>

        {/* Start Date */}
        <div className="flex-1 px-5 py-4 flex flex-col justify-between">
          <label className="text-[13px] font-semibold text-black block mb-3">{t('Start Date')}</label>
          <div className="h-9 w-full justify-start text-left font-normal text-[13px] text-gray-400 flex items-center">
            {t('Select your date')}
          </div>
        </div>

        {/* Vertical Divider */}
        <div className="self-center h-12 w-[1px] bg-gray-200"></div>

        {/* End Date */}
        <div className="flex-1 px-5 py-4 flex flex-col justify-between">
          <label className="text-[13px] font-semibold text-black block mb-3">{t('End Date')}</label>
          <div className="h-9 w-full justify-start text-left font-normal text-[13px] text-gray-400 flex items-center">
            {t('Select your date')}
          </div>
        </div>

        {/* Vertical Divider */}
        <div className="self-center h-12 w-[1px] bg-gray-200"></div>

        {/* Passengers */}
        <div className="flex-1 px-5 py-4 flex flex-col justify-between">
          <label className="text-[13px] font-semibold text-black block mb-3">
            {t('Number of Passengers')}
          </label>
          <div 
            className="h-9 text-[13px] flex items-center justify-between text-gray-700 cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary rounded-sm"
            aria-label={t('Number of Passengers')}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && onInteraction()}
          >
            {t('Number of Passengers')}
            <svg width="15" height="15" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 opacity-50"><path d="M4.93179 5.43179C4.75605 5.60753 4.75605 5.89245 4.93179 6.06819C5.10753 6.24392 5.39245 6.24392 5.56819 6.06819L7.49999 4.13638L9.43179 6.06819C9.60753 6.24392 9.89245 6.24392 10.0682 6.06819C10.2439 5.89245 10.2439 5.60753 10.0682 5.43179L7.81819 3.18179C7.73379 3.0974 7.61933 3.04999 7.49999 3.04999C7.38064 3.04999 7.26618 3.0974 7.18179 3.18179L4.93179 5.43179ZM10.0682 9.56819C10.2439 9.39245 10.2439 9.10753 10.0682 8.93179C9.89245 8.75606 9.60753 8.75606 9.43179 8.93179L7.49999 10.8636L5.56819 8.93179C5.39245 8.75606 5.10753 8.75606 4.93179 8.93179C4.75605 9.10753 4.75605 9.39245 4.93179 9.56819L7.18179 11.8182C7.26618 11.9026 7.38064 11.95 7.49999 11.95C7.61933 11.95 7.73379 11.9026 7.81819 11.8182L10.0682 9.56819Z" fill="currentColor" fillRule="evenodd" clipRule="evenodd"></path></svg>
          </div>
        </div>

        {/* Vertical Divider */}
        <div className="self-center h-12 w-[1px] bg-gray-200"></div>

        {/* Pet Allowed Checkbox */}
        <div className="flex-1 px-5 py-4 flex items-center justify-center">
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 border border-gray-300 rounded" />
            <label className="text-[13px] font-medium text-black whitespace-nowrap">
              {t('Pet Allowed')}
            </label>
          </div>
        </div>

        {/* Search Button */}
        <div className="flex items-center pr-2">
          <Button className="h-12 px-10 bg-[#1e7e34] hover:bg-[#155d27] text-white rounded-2xl font-semibold text-[15px]">
            {t('Search')}
          </Button>
        </div>
      </div>
    </div>
  );
};
